import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { useTeamRefresh } from "@/components/team/dialogs";
import { OWNER, canGrant, customerRoles } from "@/components/team/roles";
import { ErrorAlert, Form, Modal, Field, Input, CONTROL_LG, Button } from "@/components/ui";
import { DraftOffer, DraftSavedNote } from "@/components/ui/FormDraft";
import { identity } from "@/lib/api";
import { useFormDraft } from "@/lib/form-drafts";
import { INVITE_DRAFT_KEY, type InviteDraft } from "@/lib/invite-draft";
import { Hint } from "@/components/domains/parts";

const DOMAIN_MANAGER = "org_domain_manager";

/**
 * Figma "Add teammate" (4100-1275). Identity invites people to the whole workspace — roles aren't
 * scoped to one domain, and invitations carry no phone — so the "Access to" select becomes a note.
 */
export function TeammateModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { orgId, can } = useOrganization();
  const allowed = can("members:invite");
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add teammate"
      subtitle={allowed ? "They'll get an email invitation to join this workspace" : undefined}
      footer={
        allowed ? undefined : (
          <Button variant="secondary" size="lg" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      {allowed ? (
        <InviteForm orgId={orgId} onClose={onClose} />
      ) : (
        <Hint tone="warning" title="You can't invite people">
          Your role doesn't include inviting teammates. Ask a workspace Owner or Business Admin to send the
          invitation.
        </Hint>
      )}
    </Modal>
  );
}

function InviteForm({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const { permissions } = useOrganization();
  const refresh = useTeamRefresh();
  const roles = useQuery({ queryKey: ["roles", orgId], queryFn: () => identity.roles(orgId) });
  const options = customerRoles(roles.data ?? []).filter((r) => r.key !== OWNER && canGrant(r, permissions));
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const roleId = picked ?? options.find((r) => r.key === DOMAIN_MANAGER)?.id ?? options[0]?.id ?? "";
  const role = options.find((r) => r.id === roleId);
  // PHP-3874: shares its draft with the Team page's invite dialog; ready once the roles are in, so the role the
  // form settles on is where it started and not an edit.
  const kept = useFormDraft<InviteDraft>(
    INVITE_DRAFT_KEY,
    { first, last, email, roleId },
    { ready: roles.isSuccess },
  );

  const invite = useMutation({
    mutationFn: () =>
      identity.invite(orgId, {
        email: email.trim(),
        role_ids: [roleId],
        name: `${first.trim()} ${last.trim()}`.trim() || undefined,
      }),
    onSuccess: async () => {
      kept.clear();
      await refresh();
    },
  });

  if (invite.isSuccess) {
    return (
      <>
        <Hint tone="success" title="Invitation sent">
          {email.trim()} will get an email to join as {role?.name ?? "a teammate"}. You can follow it on{" "}
          <Link to="/team" className="font-semibold underline">
            Team &amp; roles
          </Link>
          .
        </Hint>
        <Button size="lg" variant="secondary" className="self-end" onClick={onClose}>
          Done
        </Button>
      </>
    );
  }

  return (
    <Form
      className="flex flex-col gap-4"
      error={invite.error}
      onSubmit={(e) => {
        e.preventDefault();
        if (roleId && email.trim()) invite.mutate();
      }}
    >
      <DraftOffer
        draft={kept}
        onContinue={(d) => {
          setFirst(d.first ?? "");
          setLast(d.last ?? "");
          setEmail(d.email ?? "");
          if (options.some((r) => r.id === d.roleId)) setPicked(d.roleId);
        }}
      />
      <div className="flex flex-col gap-3 sm:flex-row">
        <Field size="lg" label="First name" name="first_name">
          {(control) => (
            <Input
              size="lg"
              {...control}
              value={first}
              onChange={(e) => setFirst(e.target.value)}
              autoComplete="off"
            />
          )}
        </Field>
        <Field size="lg" label="Last name" name="last_name">
          {(control) => (
            <Input
              size="lg"
              {...control}
              value={last}
              onChange={(e) => setLast(e.target.value)}
              autoComplete="off"
            />
          )}
        </Field>
      </div>
      <Field size="lg" label="Email" name="email" required>
        {(control) => (
          <Input
            size="lg"
            {...control}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
          />
        )}
      </Field>
      <Field size="lg" label="User type" name="role_ids" required>
        {(control) => (
          <select
            {...control}
            className={CONTROL_LG}
            value={roleId}
            disabled={roles.isPending}
            onChange={(e) => setPicked(e.target.value)}
          >
            {roles.isPending && <option value="">Loading roles…</option>}
            {options.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <p className="rounded-xl bg-[#faf6ef] p-3 text-xs text-[#5c5551]">
        {role?.key === DOMAIN_MANAGER
          ? "Domain Managers can manage every domain in this workspace."
          : `${role?.name ?? "This role"} applies across the whole workspace, not just this domain.`}
      </p>
      <DraftSavedNote savedAt={kept.savedAt} />
      <ErrorAlert error={roles.error ?? invite.error} />
      <div className="-mx-6 -mb-6 flex flex-wrap items-center justify-end gap-2.5 border-t border-border p-6">
        <Button variant="secondary" size="lg" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="brand"
          size="lg"
          type="submit"
          loading={invite.isPending}
          disabledReason={
            roleId
              ? undefined
              : roles.isPending
                ? "Loading the roles you can give…"
                : "Your role can't give any role to a new teammate. Ask an owner to invite them."
          }
        >
          Send invite
        </Button>
      </div>
    </Form>
  );
}
